import * as Sentry from '@sentry/react';
import { getVenueProposal } from 'api/venues';
import { SpinnerOverlay } from 'components/Spinner';
import { EVENT_ROUTES, EVENT_VENUE_ROUTES, ROUTES } from 'constants/routes';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getV1Redirect } from './v1Redirect';

/**
 * Sends every legacy `/event/:eventId/...` URL to its v2 equivalent. The legacy event pages
 * no longer exist, so a v1 URL renders a spinner until the redirect lands, never the route.
 */
export const V1ToV2RedirectWrapper = ({ children }: { children: React.ReactNode }) => {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const redirect = useMemo(() => getV1Redirect(pathname, search), [pathname, search]);

    useEffect(() => {
        if (!redirect) return;

        if (redirect.type === 'path') {
            navigate(redirect.to, { replace: true });
            return;
        }

        // Proposal/messages redirect — need venue ID from API
        const { eventId, proposalId, messages } = redirect;
        let active = true;

        getVenueProposal(proposalId)
            .then(res => {
                if (!active) return;
                const venueId = res?.proposal?.venue?.id;
                if (venueId) {
                    const venueBase = `${ROUTES.EVENT}/${eventId}/${EVENT_ROUTES.VENUE}/${venueId}`;
                    const path = messages
                        ? `${venueBase}/${EVENT_VENUE_ROUTES.MESSAGES}`
                        : `${venueBase}/${EVENT_VENUE_ROUTES.PROPOSAL}/${proposalId}`;
                    navigate(path, { replace: true });
                } else {
                    Sentry.captureException(
                        new Error(`V1ToV2RedirectWrapper: Proposal ${proposalId} missing venue ID`)
                    );
                    enqueueSnackbar("We couldn't open this proposal. Redirecting you to the event.", {
                        variant: 'error',
                    });
                    navigate(`${ROUTES.EVENT}/${eventId}`, { replace: true });
                }
            })
            .catch(error => {
                if (!active) return;
                Sentry.captureException(error);
                enqueueSnackbar("We couldn't load this proposal. Redirecting to the event.", { variant: 'error' });
                navigate(`${ROUTES.EVENT}/${eventId}`, { replace: true });
            });

        return () => {
            active = false;
        };
    }, [redirect, navigate, enqueueSnackbar]);

    if (redirect) return <SpinnerOverlay />;

    return <>{children}</>;
};
